import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  User, Mail, Phone, Building2, MapPin, Briefcase, GraduationCap,
  Linkedin, Search, Users, Calendar, Edit, Save, LogOut, Star, Award, ArrowLeft, Loader2, Key
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AlumniPortal() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [profile, setProfile] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Get tenant from URL
  const getTenantCode = () => {
    const pathParts = window.location.pathname.split('/');
    // URL pattern: /tenant-code/alumni or /tenant-code/alumni-portal
    if (pathParts.length > 2 && (pathParts[2] === 'alumni' || pathParts[2] === 'alumni-portal')) {
      return pathParts[1];
    }
    // If directly accessed /alumni-portal without tenant code, return null
    if (pathParts[1] === 'alumni' || pathParts[1] === 'alumni-portal') {
      return null;
    }
    // Try from localStorage
    return localStorage.getItem('alumni_tenant_code') || null;
  };
  
  const tenantId = getTenantCode();

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    student_id: '', name: '', email: '', phone: '', password: '', confirm_password: '',
    pass_out_year: '', department: '', program: '', current_status: 'employed',
    organization: '', designation: '', package_lpa: '', location: '', linkedin_url: ''
  });
  const [editForm, setEditForm] = useState({});

  const token = localStorage.getItem('alumni_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchTenantInfo();
    if (token) {
      setIsLoggedIn(true);
      fetchProfile();
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && activeTab === 'directory') {
      fetchDirectory();
    } else if (isLoggedIn && activeTab === 'events') {
      fetchEvents();
    }
  }, [activeTab, isLoggedIn]);

  const fetchTenantInfo = async () => {
    try {
      setTenantLoading(true);
      const response = await axios.get(`${API_URL}/api/tenants/by-subdomain/${tenantId}`);
      setTenant(response.data);
    } catch (err) {
      console.error('Error fetching tenant info:', err);
    } finally {
      setTenantLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      // Get fresh token from localStorage
      const currentToken = localStorage.getItem('alumni_token');
      const authHeaders = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
      const res = await axios.get(`${API_URL}/api/alumni-portal/profile`, { headers: authHeaders });
      setProfile(res.data);
      setEditForm(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchDirectory = async () => {
    setLoading(true);
    try {
      const currentToken = localStorage.getItem('alumni_token');
      const authHeaders = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
      let url = `${API_URL}/api/alumni-portal/directory?`;
      if (searchQuery) url += `search=${searchQuery}&`;
      if (yearFilter) url += `pass_out_year=${yearFilter}&`;
      const res = await axios.get(url, { headers: authHeaders });
      setDirectory(res.data.alumni || []);
    } catch (err) {
      toast.error('Failed to fetch directory');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const currentToken = localStorage.getItem('alumni_token');
      const authHeaders = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
      const res = await axios.get(`${API_URL}/api/alumni-portal/events`, { headers: authHeaders });
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Failed to fetch events');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/alumni-portal/login`, {
        ...loginForm,
        tenant_id: tenantId
      });
      localStorage.setItem('alumni_token', res.data.access_token);
      localStorage.setItem('alumni_user', JSON.stringify(res.data.user));
      localStorage.setItem('alumni_tenant_code', tenantId);
      setIsLoggedIn(true);
      toast.success('Login successful!');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerForm.password !== registerForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...registerForm,
        pass_out_year: parseInt(registerForm.pass_out_year),
        package_lpa: registerForm.package_lpa ? parseFloat(registerForm.package_lpa) : null
      };
      delete payload.confirm_password;
      
      await axios.post(`${API_URL}/api/alumni-portal/register?tenant_id=${tenantId}`, payload);
      toast.success('Registration successful! Please wait for verification.');
      setIsRegistering(false);
      setRegisterForm({
        student_id: '', name: '', email: '', phone: '', password: '', confirm_password: '',
        pass_out_year: '', department: '', program: '', current_status: 'employed',
        organization: '', designation: '', package_lpa: '', location: '', linkedin_url: ''
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const currentToken = localStorage.getItem('alumni_token');
      const authHeaders = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
      const updateData = {
        current_status: editForm.current_status,
        organization: editForm.organization,
        designation: editForm.designation,
        industry: editForm.industry,
        package_lpa: editForm.package_lpa ? parseFloat(editForm.package_lpa) : null,
        location: editForm.location,
        phone: editForm.phone,
        linkedin_url: editForm.linkedin_url,
        achievements: editForm.achievements,
        willing_to_mentor: editForm.willing_to_mentor || false,
        areas_of_expertise: editForm.areas_of_expertise || []
      };
      
      await axios.put(`${API_URL}/api/alumni-portal/profile`, updateData, { headers: authHeaders });
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      fetchProfile();
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const savedTenantCode = localStorage.getItem('alumni_tenant_code') || tenantId;
    localStorage.removeItem('alumni_token');
    localStorage.removeItem('alumni_user');
    localStorage.removeItem('alumni_tenant_code');
    setIsLoggedIn(false);
    setProfile(null);
    toast.success('Logged out successfully');
    // Stay on the same tenant's alumni page (refresh to show login form)
    window.location.href = `/${savedTenantCode}/alumni`;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const currentToken = localStorage.getItem('alumni_token');
      await axios.post(`${API_URL}/api/alumni-portal/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    if (tenantId && tenantId !== 'alumni-portal' && tenantId !== 'alumni') {
      window.location.href = `/${tenantId}`;
    } else {
      window.location.href = '/login';
    }
  };

  const statusColors = {
    employed: 'bg-green-100 text-green-800',
    higher_studies: 'bg-blue-100 text-blue-800',
    entrepreneur: 'bg-purple-100 text-purple-800',
    seeking: 'bg-yellow-100 text-yellow-800'
  };

  // Login/Register Form
  if (!isLoggedIn) {
    if (tenantLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    // If no tenant code, show tenant code input form
    if (!tenantId) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <GraduationCap className="h-8 w-8 text-purple-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-800">Alumni Portal</CardTitle>
              <CardDescription className="text-gray-600">Enter your institution code to continue</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => {
                e.preventDefault();
                const code = e.target.tenantCode.value.trim().toLowerCase();
                if (code) {
                  window.location.href = `/${code}/alumni-portal`;
                }
              }} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tenantCode">Institution Code</Label>
                  <Input
                    id="tenantCode"
                    name="tenantCode"
                    placeholder="e.g., test730"
                    required
                  />
                  <p className="text-xs text-gray-500">Contact your institution for the code</p>
                </div>
                <Button type="submit" className="w-full">
                  Continue
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.location.href = '/login'}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Main Login
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {/* Tenant Logo & Name */}
            {tenant ? (
              <>
                {tenant.logo_url ? (
                  <img 
                    src={tenant.logo_url} 
                    alt={tenant.name} 
                    className="h-16 w-auto object-contain mx-auto mb-4"
                  />
                ) : (
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="h-8 w-8 text-white" />
                  </div>
                )}
                <h2 className="text-lg font-semibold text-gray-700 mb-2">{tenant.name}</h2>
              </>
            ) : (
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="h-8 w-8 text-white" />
              </div>
            )}
            <CardTitle className="text-2xl">{isRegistering ? 'Alumni Registration' : 'Alumni Portal'}</CardTitle>
            <p className="text-gray-500 text-sm">Connect with your alma mater</p>
          </CardHeader>
          <CardContent>
            {isRegistering ? (
              <form onSubmit={handleRegister} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Student ID" value={registerForm.student_id} onChange={(e) => setRegisterForm({...registerForm, student_id: e.target.value})} required />
                  <Input placeholder="Full Name" value={registerForm.name} onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})} required />
                </div>
                <Input type="email" placeholder="Email" value={registerForm.email} onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})} required />
                <Input placeholder="Phone" value={registerForm.phone} onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})} required />
                <div className="grid grid-cols-2 gap-3">
                  <Input type="password" placeholder="Password" value={registerForm.password} onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})} required />
                  <Input type="password" placeholder="Confirm Password" value={registerForm.confirm_password} onChange={(e) => setRegisterForm({...registerForm, confirm_password: e.target.value})} required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input type="number" placeholder="Pass Out Year" value={registerForm.pass_out_year} onChange={(e) => setRegisterForm({...registerForm, pass_out_year: e.target.value})} required />
                  <Input placeholder="Department" value={registerForm.department} onChange={(e) => setRegisterForm({...registerForm, department: e.target.value})} />
                </div>
                <Select value={registerForm.current_status} onValueChange={(v) => setRegisterForm({...registerForm, current_status: v})}>
                  <SelectTrigger><SelectValue placeholder="Current Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employed">Employed</SelectItem>
                    <SelectItem value="higher_studies">Higher Studies</SelectItem>
                    <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                    <SelectItem value="seeking">Seeking Opportunities</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Organization" value={registerForm.organization} onChange={(e) => setRegisterForm({...registerForm, organization: e.target.value})} />
                <Input placeholder="Designation" value={registerForm.designation} onChange={(e) => setRegisterForm({...registerForm, designation: e.target.value})} />
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Registering...' : 'Register'}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={() => setIsRegistering(false)}>
                  Already registered? Login
                </Button>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <Input type="email" placeholder="Email" value={loginForm.email} onChange={(e) => setLoginForm({...loginForm, email: e.target.value})} required />
                <Input type="password" placeholder="Password" value={loginForm.password} onChange={(e) => setLoginForm({...loginForm, password: e.target.value})} required />
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
                <Button type="button" variant="outline" className="w-full" onClick={() => setIsRegistering(true)}>
                  New Alumni? Register Here
                </Button>
              </form>
            )}
            
            {/* Back to Home */}
            <div className="mt-6 text-center">
              <button 
                onClick={handleBackToHome}
                className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged In View
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Alumni Portal</h1>
              <p className="text-xs text-gray-500">Welcome, {profile?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setShowChangePassword(true)} title="Change Password">
              <Key className="h-4 w-4 mr-1" /> Change Password
            </Button>
            <Button variant="ghost" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="profile"><User className="h-4 w-4 mr-1" /> My Profile</TabsTrigger>
            <TabsTrigger value="directory"><Users className="h-4 w-4 mr-1" /> Directory</TabsTrigger>
            <TabsTrigger value="events"><Calendar className="h-4 w-4 mr-1" /> Events</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            {profile && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Profile Card */}
                <Card className="md:col-span-1">
                  <CardContent className="pt-6 text-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl font-bold text-white">
                        {profile.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold">{profile.name}</h2>
                    <p className="text-gray-500">{profile.designation || 'Alumni'}</p>
                    <p className="text-sm text-gray-400">{profile.organization}</p>
                    <Badge className={`mt-2 ${statusColors[profile.current_status]}`}>
                      {profile.current_status?.replace('_', ' ')}
                    </Badge>
                    
                    <div className="mt-4 space-y-2 text-left text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="h-4 w-4" /> {profile.email}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="h-4 w-4" /> {profile.phone}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <GraduationCap className="h-4 w-4" /> Batch of {profile.pass_out_year}
                      </div>
                      {profile.location && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="h-4 w-4" /> {profile.location}
                        </div>
                      )}
                    </div>
                    
                    {profile.linkedin_url && (
                      <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-blue-600 text-sm">
                        <Linkedin className="h-4 w-4" /> LinkedIn Profile
                      </a>
                    )}
                  </CardContent>
                </Card>

                {/* Edit Profile */}
                <Card className="md:col-span-2">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Profile Information</CardTitle>
                    {!isEditing && (
                      <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {isEditing ? (
                      <form onSubmit={handleProfileUpdate} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">Current Status</label>
                            <Select value={editForm.current_status} onValueChange={(v) => setEditForm({...editForm, current_status: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="employed">Employed</SelectItem>
                                <SelectItem value="higher_studies">Higher Studies</SelectItem>
                                <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                                <SelectItem value="seeking">Seeking Opportunities</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-sm font-medium">Organization</label>
                            <Input value={editForm.organization || ''} onChange={(e) => setEditForm({...editForm, organization: e.target.value})} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">Designation</label>
                            <Input value={editForm.designation || ''} onChange={(e) => setEditForm({...editForm, designation: e.target.value})} />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Industry</label>
                            <Input value={editForm.industry || ''} onChange={(e) => setEditForm({...editForm, industry: e.target.value})} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">Package (LPA)</label>
                            <Input type="number" step="0.1" value={editForm.package_lpa || ''} onChange={(e) => setEditForm({...editForm, package_lpa: e.target.value})} />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Location</label>
                            <Input value={editForm.location || ''} onChange={(e) => setEditForm({...editForm, location: e.target.value})} />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium">Phone</label>
                          <Input value={editForm.phone || ''} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} />
                        </div>
                        <div>
                          <label className="text-sm font-medium">LinkedIn URL</label>
                          <Input value={editForm.linkedin_url || ''} onChange={(e) => setEditForm({...editForm, linkedin_url: e.target.value})} />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Achievements</label>
                          <Textarea value={editForm.achievements || ''} onChange={(e) => setEditForm({...editForm, achievements: e.target.value})} placeholder="Share your achievements..." />
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox checked={editForm.willing_to_mentor || false} onCheckedChange={(c) => setEditForm({...editForm, willing_to_mentor: c})} />
                          <label className="text-sm">I&apos;m willing to mentor current students</label>
                        </div>
                        <div className="flex gap-2">
                          <Button type="submit" disabled={loading}>
                            <Save className="h-4 w-4 mr-1" /> Save Changes
                          </Button>
                          <Button type="button" variant="outline" onClick={() => { setIsEditing(false); setEditForm(profile); }}>
                            Cancel
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div><span className="text-gray-500 text-sm">Organization</span><p className="font-medium">{profile.organization || '-'}</p></div>
                          <div><span className="text-gray-500 text-sm">Designation</span><p className="font-medium">{profile.designation || '-'}</p></div>
                          <div><span className="text-gray-500 text-sm">Industry</span><p className="font-medium">{profile.industry || '-'}</p></div>
                          <div><span className="text-gray-500 text-sm">Package</span><p className="font-medium">{profile.package_lpa ? `₹${profile.package_lpa} LPA` : '-'}</p></div>
                          <div><span className="text-gray-500 text-sm">Location</span><p className="font-medium">{profile.location || '-'}</p></div>
                          <div><span className="text-gray-500 text-sm">Department</span><p className="font-medium">{profile.department || '-'}</p></div>
                        </div>
                        {profile.achievements && (
                          <div><span className="text-gray-500 text-sm">Achievements</span><p className="font-medium">{profile.achievements}</p></div>
                        )}
                        {profile.willing_to_mentor && (
                          <Badge className="bg-green-100 text-green-800"><Star className="h-3 w-3 mr-1" /> Available for Mentoring</Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Directory Tab */}
          <TabsContent value="directory">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle>Alumni Directory</CardTitle>
                  <div className="flex gap-2">
                    <Input placeholder="Search by name or organization..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-64" />
                    <Input type="number" placeholder="Year" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} className="w-24" />
                    <Button onClick={fetchDirectory}><Search className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {directory.map((alum, idx) => (
                    <Card key={idx} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white font-bold">
                            {alum.name?.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium">{alum.name}</h3>
                            <p className="text-sm text-gray-500">{alum.designation}</p>
                            <p className="text-sm text-gray-400">{alum.organization}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                              <Badge variant="outline">Batch {alum.pass_out_year}</Badge>
                              {alum.location && <span><MapPin className="h-3 w-3 inline" /> {alum.location}</span>}
                            </div>
                            {alum.willing_to_mentor && (
                              <Badge className="mt-2 bg-green-100 text-green-800 text-xs"><Star className="h-3 w-3 mr-1" /> Mentor</Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {directory.length === 0 && !loading && (
                    <div className="col-span-full text-center py-8 text-gray-500">
                      No alumni found. Try adjusting your search.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Events</CardTitle>
              </CardHeader>
              <CardContent>
                {events.length > 0 ? (
                  <div className="space-y-4">
                    {events.map((event, idx) => (
                      <div key={idx} className="p-4 border rounded-lg">
                        <h3 className="font-medium">{event.title}</h3>
                        <p className="text-sm text-gray-500">{event.description}</p>
                        <div className="flex gap-4 mt-2 text-sm text-gray-400">
                          <span><Calendar className="h-4 w-4 inline mr-1" />{event.event_date}</span>
                          <span><MapPin className="h-4 w-4 inline mr-1" />{event.venue}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No upcoming events</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Change Password Modal */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" /> Change Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Current Password *</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                required
                placeholder="Enter current password"
              />
            </div>
            <div>
              <Label>New Password *</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                required
                minLength={6}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            <div>
              <Label>Confirm New Password *</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                required
                placeholder="Confirm new password"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowChangePassword(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Change Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
